/** The game's sounds, in public/audio: several takes of a hit, so the same punch never sounds twice the same. */
const files = {
  punch: ['punch-0', 'punch-1', 'punch-2'],
  block: ['block-0', 'block-1', 'block-2'],
  bell: ['bell'],
  whoosh: ['whoosh'],
  cheer: ['crowd-cheer'],
  roar: ['crowd-roar'],
} as const;
const ambience = 'crowd-ambience';

export type SoundName = keyof typeof files;

export interface PlayOptions {
  readonly volume?: number;
  readonly rate?: number;
  /** Seconds from now. */
  readonly delay?: number;
}

/**
 * Web Audio for the fight: short sounds played on the rules' events, and the hall's hum under everything, louder as
 * the crowd gets roused. Browsers only let a page make sound after the player touches it, so the first press or key
 * wakes it; until then, and for any sound that failed to load, the game is simply quiet.
 */
export class Sound {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private hum: GainNode | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();
  private loading = false;
  private humming = false;
  private muted = false;

  constructor() {
    // A hidden tab stops the game, so the hall falls quiet with it and comes back when the tab does.
    document.addEventListener('visibilitychange', () => {
      if (!this.context) return;
      if (document.hidden) void this.context.suspend();
      else void this.context.resume();
    });
  }

  /** Starts sound on the player's first touch or key; later calls fetch whatever failed to load before. */
  wake(): void {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.value = this.muted ? 0 : 0.9;
      this.master.connect(this.context.destination);
      this.hum = this.context.createGain();
      this.hum.gain.value = 0;
      this.hum.connect(this.master);
    }
    if (this.context.state === 'suspended') void this.context.resume();
    if (!this.loading) void this.load();
  }

  play(name: SoundName, options: PlayOptions = {}): void {
    const takes = files[name];
    const buffer = this.buffers.get(takes[Math.floor(Math.random() * takes.length)]);
    if (!this.context || !this.master || !buffer) return;
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = (options.rate ?? 1) * (0.96 + Math.random() * 0.08);
    const gain = this.context.createGain();
    gain.gain.value = options.volume ?? 1;
    source.connect(gain).connect(this.master);
    source.start(this.context.currentTime + (options.delay ?? 0));
  }

  /** How roused the crowd is, 0..1: the hum swells with it. */
  crowd(level: number): void {
    if (!this.context || !this.hum) return;
    this.hum.gain.setTargetAtTime(0.22 + 0.4 * level, this.context.currentTime, 0.25);
  }

  /** Sound off or back on; true when it is now on. */
  toggle(): boolean {
    this.muted = !this.muted;
    if (this.context && this.master) this.master.gain.setTargetAtTime(this.muted ? 0 : 0.9, this.context.currentTime, 0.05);
    return !this.muted;
  }

  private async load(): Promise<void> {
    this.loading = true;
    const names = [...Object.values(files).flat(), ambience].filter((name) => !this.buffers.has(name));
    await Promise.all(names.map((name) => this.fetchSound(name)));
    this.loading = false;
    this.startHum();
  }

  // A sound that fails to arrive is left out, not remembered as missing: the next wake tries it again.
  private async fetchSound(name: string): Promise<void> {
    try {
      const response = await fetch(`${import.meta.env.BASE_URL}audio/${name}.mp3`);
      if (!response.ok) return;
      this.buffers.set(name, await this.context!.decodeAudioData(await response.arrayBuffer()));
    } catch {
      // Quiet instead of broken: the fight goes on without this sound.
    }
  }

  private startHum(): void {
    const buffer = this.buffers.get(ambience);
    if (this.humming || !buffer || !this.context || !this.hum) return;
    this.humming = true;
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    // An mp3 carries a sliver of silence at each end; looping inside them keeps the hum seamless.
    source.loopStart = 0.06;
    source.loopEnd = buffer.duration - 0.06;
    source.connect(this.hum);
    source.start(0, source.loopStart);
    this.crowd(0);
  }
}
